import { action, toJS } from "mobx";
import { CommonStore } from "common/CommonStore";
import axios from "axios";
import { FirebaseDocument } from "common/models/FirebaseDocument";
import {
  createRateLibraryModelFromDocument,
  RateLibrary,
} from "modules/activitySearch/models/RateLibrary";
import { WBSTasks } from "modules/WBS/models/WBSTasks";
import { WBSResource } from "modules/WBS/models/WBSResource";
import { ActivitySearchService } from "modules/activitySearch/ActivitySearchService";
import { WBSService } from "modules/WBS/WBSService";
import { CalculateWBSService } from "modules/WBS/CalculateWBSService";
import { WBSStore } from "modules/WBS/WBSStore";
import { WBSModels } from "modules/WBS/WBSModels";
import { ToolbarTaskStore } from "modules/WBS/ToolbarTaskStore";
import { ToolbarStore } from "modules/WBS/ToolbarStore";
import { WBSPropertiesStore } from "modules/WBS/WBSPropertiesStore";
import { CustomPropertyStore } from "modules/customProperties/CustomPropertyStore";
import { RateLibraryTasks } from "modules/activitySearch/models/RateLibraryTasks";
import { EContentType, EContentTypeText, ERowType } from "common/enums";
import { alternativeCodeLabel, resourceGroupLabel } from "common/constants";
import * as _ from "lodash";
import { roundBy, subtract, sum, times } from "utils/DecimalUtils";

interface IAction {
  row?: any;
  type: string;
  args: any[];
}

class Store {
  forceUpdate: Function;
  snapshotEventFired = false;
  wbsTasks: Array<FirebaseDocument<WBSTasks>> = [];
  wbsResources: Array<FirebaseDocument<WBSResource>> = [];
  rateLibraries: Array<FirebaseDocument<RateLibrary>> = [];
  rateLibrariesActive: Array<Array<any>> = [];
  search: string = "";
  filteredTasksDispatch: boolean = false;
  clientInfoResources: any = [];
  clientInfoTasks: any = [];
  reuseRateLibraries = new Object();
  newTask: any = [];

  newLineID: string = "";

  activities;
  promises: Array<Promise<any>>;

  resetStore() {
    this.snapshotEventFired = false;
    this.wbsTasks = [];
    this.wbsResources = [];
    this.rateLibraries = [];
    this.search = "";
    this.filteredTasksDispatch = false;
    this.newLineID = "";
    this.activities = new Array();
    this.promises = new Array<Promise<any>>();
  }

  @action
  async getFilteredTasks(search, wbsChecked) {
    CommonStore.loadingInputSearch = true;
    this.filteredTasksDispatch = true;
    let promises: Array<Promise<any>> = [];
    if (wbsChecked) {
      promises.push(
        new Promise<void>(async (resolve) => {
          this.wbsTasks = await WBSService.filterWBSActivity(
            ERowType.TASK,
            search
          );
          resolve();
        })
      );
      promises.push(
        new Promise<void>(async (resolve) => {
          this.wbsResources = await WBSService.filterWBSActivity(
            ERowType.RESOURCE,
            search
          );
          resolve();
        })
      );
    }
    for (let { id, data } of this.rateLibraries)
      if (data.active)
        promises.push(
          new Promise<void>(async (resolve) => {
            data.tasks = await ActivitySearchService.fetchRateLibrariesTasks(
              id,
              search
            );
            resolve();
          })
        );

    await Promise.all(promises);
    this.forceUpdate();
    if (search !== this.search)
      if (this.search.length >= 2)
        this.getFilteredTasks(this.search, wbsChecked);
      else this.clearFilteredTasks();
    else this.filteredTasksDispatch = false;
    CommonStore.loadingInputSearch = false;
  }

  /** Obtém as atualizações dos banco de composições do usuário, os que são compartilhados com ele
   * e os bancos públicos que o mesmo segue.
   * Os itens são adicionados em ordem de data de modificação, atualizados e removidos da propriedade
   * rateLibraries.
   */
  fetchRateLibraries() {
    if (!this.snapshotEventFired) {
      this.snapshotEventFired = true;
      this.rateLibraries = [];

      ActivitySearchService.fetchRateLibrariesFromFirebase((docsChange) => {
        for (const change of docsChange) {
          if (change.doc.id === null) throw new Error("doc.id is null");
          if (change.doc.data().status !== "processed") continue;

          const rateLibraries = this.rateLibraries;
          const item = createRateLibraryModelFromDocument(change.doc);

          if (change.type === "added") {
            const compare = (a, b) => a < b;
            CommonStore.pushSortingByField(
              rateLibraries,
              item,
              ["data", "modifiedAt", "seconds"],
              compare
            );
          } else {
            const index = rateLibraries.findIndex((lib) => lib.id === item.id);
            if (index !== -1) {
              if (change.type === "removed") {
                rateLibraries.splice(index, 1);
              } else {
                rateLibraries[index] = item;
              }
            }
          }

          this.setRateLibraries(rateLibraries);
        }
      });
    }
  }

  @action
  setRateLibraries(rateLibraries: FirebaseDocument<RateLibrary>[]) {
    this.rateLibraries = rateLibraries;
  }

  clearFilteredTasks = () => {
    this.filteredTasksDispatch = false;
    for (let { data } of this.rateLibraries) data.tasks = [];
    this.wbsTasks = [];
    this.wbsResources = [];
    this.forceUpdate();
  };

  @action
  handleClean = () => {
    ActivitySearchStore.search = "";
    if (!ActivitySearchStore.filteredTasksDispatch) {
      this.clearFilteredTasks();
    }
  };

  /**
   * Função assíncrona para manipular a atualização do conteúdo de uma linha do plano de custo de acordo com a atividade selecionada.
   * Atenção: Alterações nesta função podem demandar alterações também na função handleSelectedFromModelMap
   * @param activity Atividade selecionada
   * @param origin Origem da atividade (plano de custo ou banco de composição)
   */
  async handleSelected(activity, origin) {
    const selectedIndex = WBSStore.selectedLine.index;
    if (selectedIndex === -1) return;

    WBSStore.isLoadingOperations = true;
    if (!WBSStore.updateResource) WBSStore.updateResource = true;

    const actions = new Array<IAction>();
    const linesToDelete: any = [];
    const currentSelectedLine = {
      ...WBSStore.selectedLine,
      data: { ...WBSStore.selectedLine.data },
    };

    // Se for model-map full procura todas as cópias
    const isModelMapFull = Boolean(currentSelectedLine.data.modelMapFull);
    const allSameContentModelMap = WBSStore.getListByContent(
      isModelMapFull ? currentSelectedLine.data.contentId : undefined,
      currentSelectedLine.id
    );

    for (const lineFounded of allSameContentModelMap) {
      // Define a linha a ser edita como selecionada
      await WBSStore.setSelectedLineById(lineFounded.id);
      let selectedLine = { ...WBSStore.selectedLine };
      linesToDelete.push(selectedLine);

      // Se vier do banco de composições, verifica se já não possue
      // uma cópia no plano de custo
      if (origin === "RL") {
        const activityType = activity.data.type === "TASK" ? 1 : 2;
        const activityWithSameCode = WBSStore.WBSRows.find(
          (el) =>
            el.data.rateLibraryId === activity.data.file.id &&
            el.data.rateLibraryCode === activity.data.code &&
            el.data.rowType === activityType
        );
        if (activityWithSameCode) {
          activity = activityWithSameCode;
          origin = "WBS";
        }
      }

      // Cria linha com dados padrão
      const defaultRow = this.createDefaultRow(
        selectedLine.data,
        activity.data
      );
      // Verifica se a linha vinda do model-map é Etapa, caso seja interrompe o processo
      if (
        defaultRow.fromModelMap &&
        selectedLine.data.rowType === ERowType.STAGE
      ) {
        WBSStore.isLoadingOperations = false;
        return;
      }

      WBSStore.loading = true;
      WBSStore.loadingInsertTask = true;

      // Se não vier do banco de composições, só precisa copiar e colar
      if (origin !== "RL") {
        // Copiar e colar a atividade
        if (selectedLine.data.parentTask) {
          delete activity.data.parentStage;
          await ToolbarTaskStore.copyTaskRow(activity, true);
          await ToolbarTaskStore.pasteTaskRow(true);
        } else {
          delete activity.data.parentTask;
          await ToolbarStore.copyWBSRow(activity);
          await ToolbarStore.pasteWBSRow(
            true,
            undefined,
            selectedLine.data.modelMapFull
          );
        }
        /* Recupera o conteúdo da atividade selecionada a partir de um banco de composições */
      } else {
        const rules = WBSPropertiesStore.getRules(2);
        let newUnitCost = 0;
        // Recalculando o total do item da composição para poder aplicar corretamente a regra de arredondamento
        if (activity.data.composition.length > 0) {
          activity.data.composition = activity.data.composition.map((comp) => {
            if (comp) {
              /* Verificar se no o coefficient tem ",".
                  Caso não tenha calcula o totalCost normalmente
                  Caso tenha troca a "," por "." para poder calcular o totalCost[já que "," não é aceita em números e im "."]
              */
              let convertString = comp.coefficient.toString();
              if (convertString.search(/[,]/g) === -1) {
                comp.totalCost = times(comp.coefficient, comp.unityCost, rules);
              } else {
                comp.totalCost = times(
                  comp.coefficient.toString().replace(",", "."),
                  comp.unityCost,
                  rules
                );
              }
              newUnitCost = sum(newUnitCost, comp.totalCost);
            }
            return comp;
          });
        } else {
          activity.data.totalCost = roundBy(activity.data.totalCost, rules);
          newUnitCost = activity.data.totalCost;
        }

        // Atualiza os dados da linha a ser inserida
        if (selectedLine.data.modelMapFull) {
          defaultRow.quantity = selectedLine.data.quantity || 0;
          defaultRow.modelMapDescription =
            selectedLine.data.modelMapDescription;
          defaultRow.modelMapFull = selectedLine.data.modelMapFull;
          defaultRow.contentId = selectedLine.data.contentId;
        }

        defaultRow.unitCost = roundBy(newUnitCost, rules);
        defaultRow.total = times(
          defaultRow.unitCost,
          defaultRow.quantity,
          rules
        );

        if (EContentTypeText.LABOR == activity.data.class)
          defaultRow.contentType = EContentType.LABOR;
        if (EContentTypeText.MATERIAL == activity.data.class)
          defaultRow.contentType = EContentType.MATERIAL;
        if (EContentTypeText.EQUIPMENT == activity.data.class)
          defaultRow.contentType = EContentType.EQUIPMENT;
        if (EContentTypeText.TASK == activity.data.class)
          defaultRow.contentType = EContentType.TASK;
        if (EContentTypeText.OTHER == activity.data.class)
          defaultRow.contentType = EContentType.OTHER;

        // Recupera os dados da linha pai da linha selecionada
        const hasTaskParent = Boolean(selectedLine.data.parentTask);
        const parentRow = WBSStore.WBSRows.find((r) =>
          hasTaskParent
            ? r.id == selectedLine.data.parentTask
            : r.id == selectedLine.data.parentStage
        );

        // Insere as linhas do banco de composições na linha seleciona
        // replicando nas cópias da linha pai caso aja
        if (parentRow) {
          const rowIndex = selectedLine.data.rowIndex;
          const parentsWithSameContent = WBSStore.getListByContent(
            parentRow.data.contentId,
            parentRow.id
          );

          let selectedLineOfParent = {
            ...selectedLine,
            data: { ...selectedLine.data },
          };

          for (const parent of parentsWithSameContent) {
            selectedLineOfParent = this.findLineByParentID(
              parent.id,
              rowIndex,
              selectedLineOfParent
            );

            const lineActions = await this.includeRateLibraryItem(
              activity,
              { ...defaultRow },
              { ...selectedLineOfParent }
            );

            // Recálcula o conteúdo da composição e adiciona o valor do BDI com os dados atuais
            const lineID =
              this.newLineID !== "" ? this.newLineID : selectedLineOfParent.id;
            const results = await this.recalculateValuesOfActivity(
              lineID,
              selectedLineOfParent,
              lineActions
            );
            actions.push(...lineActions);

            let rowDiffBdi = results.diffBdi;
            let rowNewTotal = results.newTotal;

            // Atualiza o total das linhas acima recursivamente
            const parentCurrentTotal = hasTaskParent
              ? parentRow.data.unitCost || 0
              : parentRow.data.total || 0;

            let newData: any = await WBSStore.recursiveUpdateWBSSubcollection(
              undefined,
              sum(rowNewTotal, parentCurrentTotal, rules),
              /** Quando o pai for composição é passado undefined para que a função
               * refaça a conta em cada cópia */
              hasTaskParent ? undefined : parentCurrentTotal,
              parentRow.id,
              true,
              rowDiffBdi
            );
            actions.push(...newData);
          }
        } else {
          const lineActions = await this.includeRateLibraryItem(
            activity,
            { ...defaultRow },
            { ...selectedLine }
          );

          // Recálcula o conteúdo da composição e adiciona o valor do BDI com os dados atuais
          const lineID =
            this.newLineID !== "" ? this.newLineID : selectedLine.id;
          await this.recalculateValuesOfActivity(
            lineID,
            selectedLine,
            lineActions
          );
          actions.push(...lineActions);

          const globalBDI = WBSStore.WBS.data.bdi.percentual;
          let totalBdi = CalculateWBSService.calculateBdiTotalSale(
            this.newTask.total,
            globalBDI,
            WBSPropertiesStore.wbsCalculate.roundingRule
          );
          actions.push(WBSStore.setNewWbsTotal(this.newTask.total, totalBdi));
        }

        this.newLineID = "";
      }
    }

    // Padroniza o custo unitários dos itens com mesmo contentId
    for (let item of actions) {
      let data = item.args[1];
      let find = actions.filter((a) => a.args[1].contentId == data.contentId);

      if (find.length > 1) {
        let unitCost = find[0].args[1].unitCost;
        let hasDiff = find.some((f) => f.args[1].unitCost != unitCost);

        if (hasDiff) {
          let unitCost = find.reduce((a, b) => {
            return a.args[1].unitCost > b.args[1].unitCost ? a : b;
          }).args[1].unitCost;

          for (let itemFound of find) {
            itemFound.args[1].unitCost = unitCost;
          }
        }
      }
    }

    // Persiste os dados no firebase e atualiza as tabelas
    if (actions.length) {
      await WBSStore.batchActions(actions, false);

      if (isModelMapFull) {
        for (const line of linesToDelete) {
          await WBSStore.setSelectedLineById(line.id);
          await ToolbarStore.deleteWBSRow(true);
        }
      }

      if (currentSelectedLine.data.parentTask) {
        WBSStore.taskRows = [];
        WBSStore.updateTaskRows(WBSStore.task);
      } else {
        WBSStore.loadDatatableWithRightFormat(WBSStore.WBSRows);
      }

      WBSStore.selectedLine = { ...currentSelectedLine };
    }

    WBSStore.loadingInsertTask = false;
    WBSStore.loading = false;
  }

  private findLineByParentID(parentId: string, rowIndex: number, line) {
    line = { ...line, data: { ...line.data } };

    if (line.data.parentTask) {
      const index = WBSStore.WBSRows.findIndex(
        (r) => r.data.rowIndex == rowIndex && r.data.parentTask == parentId
      );
      if (index !== -1) {
        line = {
          ...WBSStore.WBSRows[index],
          index: -1,
        };
      }
    } else if (line.data.parentStage) {
      const index = WBSStore.WBSRows.findIndex(
        (r) => r.data.rowIndex == rowIndex && r.data.parentStage == parentId
      );
      if (index !== -1) {
        line = {
          ...WBSStore.WBSRows[index],
          index: -1,
        };
      }
    }

    if (line.data.parentTask) line.data.parentTask = parentId;
    if (line.data.parentStage) line.data.parentStage = parentId;
    return line;
  }

  public recalculateActivityComposition(activity) {
    let newUnitCost = 0;
    let composition = activity.data.composition.map((comp) => {
      if (comp) {
        /* Verificar se no o coefficient tem ",".
            Caso não tenha calcula o totalCost normalmente
            Caso tenha troca a "," por "." para poder calcular o totalCost[já que "," não é aceita em números e im "."]
        */
        let convertString = comp.coefficient.toString();
        if (convertString.search(/[,]/g) === -1) {
          comp.totalCost = times(comp.coefficient, comp.unityCost);
        } else {
          comp.totalCost = times(
            comp.coefficient.toString().replace(",", "."),
            comp.unityCost
          );
        }
        newUnitCost = sum(newUnitCost, comp.totalCost);
      }
      return comp;
    });
    return { composition, newUnitCost };
  }

  private async recalculateValuesOfActivity(
    lineId: string,
    selectedLine: any,
    actions: IAction[]
  ) {
    let diffBdi = 0;
    let newTotal = 0;
    const rules = WBSPropertiesStore.getRules(2);
    const actionToInsert = actions.find(({ args }) => args[0].id === lineId);

    if (actionToInsert) {
      const [_, row] = actionToInsert.args;

      // Recálcula o conteúdo da composição caso tenha vindo do banco de composições
      if (row.rowType === ERowType.TASK) {
        const taskCollection = this.mountTaskCollection(actions);
        await this.calculateTask(actionToInsert, taskCollection);
      }

      const newUnitCost = roundBy(row.unitCost, rules);
      newTotal = times(newUnitCost, row.quantity, rules);

      row.unitCost = newUnitCost;
      row.total = newTotal;

      WBSStore.updateTaskUnitCost(row.total);

      const bdiValue = WBSStore.getBdiForRow(row);
      const result = CalculateWBSService.calculateRowBdi(
        row,
        row.total,
        bdiValue,
        WBSPropertiesStore.wbsCalculate
      );

      let totalSale = result ? result.totalSale : 0;
      let unitCostSale = result ? result.unitCostSale : 0;

      if (selectedLine.data.parentStage) {
        diffBdi = subtract(totalSale, row.totalBdi);
        row.totalBdi = totalSale;
        if (selectedLine.data.rowType !== ERowType.STAGE) {
          row.vendaBdi = unitCostSale;
        }
      }
    }

    return { diffBdi, newTotal };
  }

  private mountTaskCollection(activity: IAction[] = []) {
    const tasksInside = activity.filter(
      (row) => row.args[1].rowType === ERowType.TASK
    );
    const resourcesInside = activity.filter(
      (row) => row.args[1].rowType == ERowType.RESOURCE
    );
    return { tasksInside, resourcesInside };
  }

  private async calculateTask(
    task: IAction,
    wbsCollection: { tasksInside: IAction[]; resourcesInside: IAction[] }
  ) {
    if (!task) return;
    const taskId = task.args[0].id;

    const tasksInside = this.getChilrenOfTask(
      taskId,
      wbsCollection.tasksInside
    );
    if (tasksInside.length) {
      // Primeiro recalcula todas as tasks filhas
      await Promise.all(
        tasksInside.map((tsk: IAction) =>
          this.calculateTask(tsk, wbsCollection)
        )
      );
    }

    // Soma o total das tasks filhas
    const taskTotal = tasksInside.reduce(
      (total: any, tsk: IAction) => sum(total, tsk.args[1].total || 0),
      0
    );

    // Soma o total das resources filhas
    const resourcesInside = this.getChilrenOfTask(
      taskId,
      wbsCollection.resourcesInside
    );
    const resourcesTotal = resourcesInside.reduce(
      (total: any, resource: IAction) =>
        sum(total, resource.args[1].total || 0),
      0
    );

    // Recalcula a Task
    task = this.calculateRow(task, sum(taskTotal, resourcesTotal));
  }

  private getChilrenOfTask(id, list: IAction[]) {
    return list.filter((el) => el.args[1].parentTask == id);
  }

  private calculateRow(action: IAction, newUnitCost: number) {
    const rules = WBSPropertiesStore.getRules(2);
    const row = action.args[1];
    const quantity = row.quantity || 0;
    const unitCost = roundBy(newUnitCost, rules);
    const total = times(unitCost || 0, quantity, rules);

    row.unitCost = unitCost;
    row.total = total;

    const bdi = WBSStore.getBdiForRow(row);
    const result = CalculateWBSService.calculateRowBdi(
      row,
      total,
      bdi,
      WBSPropertiesStore.wbsCalculate
    );

    row.vendaBdi = result ? result.unitCostSale : 0;
    row.totalBdi = result ? result.totalSale : 0;
    return action;
  }

  private createDefaultRow(lineData, activityData) {
    let description = lineData.modelMapFull
      ? lineData.description
      : activityData.description;
    // PRUBK-826 - Unidade de medida errada ao incluir composição no orçamento
    let unit =
      activityData.unity && activityData.unity !== lineData.unit
        ? activityData.unity
        : lineData.unit;
    let defaultRow = WBSModels.getDefaultRow({
      description: description,
      category: activityData.class,
      unit: unit,
      rowIndex: lineData.rowIndex,
      quantity: 0,
      rowType:
        activityData.type == "RESOURCE" ? ERowType.RESOURCE : ERowType.TASK,
    });

    if (lineData.modelMapFull && lineData.fromModelMap) {
      defaultRow.fromModelMap = lineData.fromModelMap;
    }

    return { ...defaultRow };
  }

  /**
   * Adiciona a linha de composição dentro da EAP, chamando o serviço para fazer a inserção
   * @param activity Linha direto do banco de composição
   * @param defaultRow Linha que será criada, contem os dados do activity e da selectedLine compilados em uma unica variavel que será usada para criar a nova linha
   * @param selectedLine Linha selecionada da EAP
   * @returns
   */
  private async includeRateLibraryItem(
    activity: FirebaseDocument<RateLibraryTasks>,
    defaultRow,
    selectedLine
  ) {
    const actions = new Array();
    let tasksList: any = [];
    defaultRow.rateLibraryId = activity.data.file.id;
    defaultRow.rateLibraryCode = activity.data.code;
    defaultRow.rateLibraryName = activity.data.description;
    defaultRow.rowCode = activity.data.code;

    defaultRow = this.setExistingActivityDataToRow(
      defaultRow,
      defaultRow.rateLibraryId,
      defaultRow.rateLibraryCode
    );
    defaultRow = this.setSelectedLineDataToRow(defaultRow, selectedLine);

    // Cria as propriedades do item se não já não houver
    // de uma ocorrência existente
    if (!defaultRow.customProperties) {
      defaultRow.customProperties =
        await this.createNeededCustomPropertiesOfRateLibraryItem(activity.data);
    }

    let rowRef: any = undefined;
    if (selectedLine.data.rowType == ERowType.BLANK) {
      rowRef = WBSStore.WBSRef.collection("rows").doc(selectedLine.id);
      actions.push(
        this.insertActivityAtBlankRowAndUpdateRows(
          rowRef,
          defaultRow,
          selectedLine
        )
      );
    } else {
      rowRef = WBSStore.WBSRef.collection("rows").doc();
      actions.push(
        ...this.insertActivityRowAtEndAndUpdateRows(rowRef, defaultRow)
      );
    }

    if (rowRef) {
      actions.push(
        ...(await this.createActivitySubItems(
          rowRef.id,
          activity,
          defaultRow.rateLibraryId,
          tasksList
        ))
      );
    }

    this.newTask = defaultRow;
    return actions;
  }

  /**
   * Verifica se o item (insumo/composição) do banco de composições
   * possui as propriedades de código alternativo e/ou descrição do
   * grupo. Caso tenha, adiciona no objeto de propriedades customizadas
   * que será retornado.
   * @param activityItem Dados do item principal ou de seu conteúdo proveniente da propriedade compostition
   * @returns {Promise<{ [propertyId: string]: string }>} Objeto com as propriedades personalizadas
   */
  async createNeededCustomPropertiesOfRateLibraryItem(
    activityItem: Partial<{
      descriptionGroup: string;
      alternativeCodeItem: string;
      alternativeCodeComposition: string;
    }>
  ): Promise<{ [propertyId: string]: string }> {
    const groupName = activityItem.descriptionGroup;
    const alternativeCode = activityItem.alternativeCodeItem
      ? activityItem.alternativeCodeItem
      : activityItem.alternativeCodeComposition;

    let customProperties = {};
    if (groupName) {
      const groupPropertyId = await CustomPropertyStore.getOrAddCustomProperty(
        resourceGroupLabel
      );
      if (groupPropertyId) customProperties[groupPropertyId] = groupName;
    }

    if (alternativeCode) {
      const codePropertyId = await CustomPropertyStore.getOrAddCustomProperty(
        alternativeCodeLabel
      );
      if (codePropertyId) customProperties[codePropertyId] = alternativeCode;
    }
    return customProperties;
  }

  private insertActivityAtBlankRowAndUpdateRows(
    rowRef,
    rowData,
    selectedLine
  ): IAction {
    WBSStore.changeAllArray({ ...rowData }, selectedLine, false);
    return {
      type: "update",
      args: [rowRef, rowData],
    };
  }

  private insertActivityRowAtEndAndUpdateRows(rowRef, rowData): IAction[] {
    const actions = new Array<IAction>();
    const { parentStage, parentTask } = rowData;

    if (parentStage) {
      actions.push(
        ...ToolbarStore.changeRowIndex(rowData.rowIndex, parentStage)
      );
    } else {
      actions.push(
        ...ToolbarTaskStore.changeRowIndex(rowData.rowIndex, parentTask)
      );
    }

    this.newLineID = rowRef.id;
    WBSStore.insertRowAtEnd(rowRef.id, rowData);
    actions.push({
      type: "set",
      args: [rowRef, rowData],
    });

    return actions;
  }

  private setExistingActivityDataToRow(
    row,
    rateLibraryId: string,
    rateLibraryCode: string
  ): any {
    const existingActivity = WBSStore.findActivityRow(
      rateLibraryId,
      rateLibraryCode,
      row.rowType
    );
    let changQuantity = false;
    let indexKey: number = 0;

    if (existingActivity.length != 0) {
      let parent: any = WBSStore.WBSRows.filter((el) => {
        for (let i = 0; i < existingActivity.length; i++) {
          if (existingActivity[i].data.parentTask == el.id) return true;
        }
        return false;
      });
      let parentRow = WBSStore.WBSRows.find((el) => el.id == row.parentTask);
      if (parentRow) {
        for (let i = 0; i < parent.length; i++) {
          if (
            parentRow.data.rateLibraryCode == parent[i].data.rateLibraryCode
          ) {
            changQuantity = true;
            indexKey = i;
          }
        }
      }
      if (parent[indexKey]) {
        let verify = existingActivity.findIndex(
          (el) => parent[indexKey].id === el.data.parentTask
        );
        indexKey = verify;
      }
    }

    if (existingActivity.length > 0) {
      const unitCost = existingActivity[indexKey].data.unitCost || 0;
      const quantity = existingActivity[indexKey].data.quantity || 0;

      if (existingActivity[indexKey].data.rowCode)
        row.rowCode = existingActivity[indexKey].data.rowCode;

      row.contentId = existingActivity[indexKey].data.contentId;
      row.description = existingActivity[indexKey].data.description;
      row.unit = existingActivity[indexKey].data.unit;
      row.unitCost = unitCost;
      row.contentType = existingActivity[indexKey].data.contentType;

      const rules = WBSPropertiesStore.getRules(2);
      if (changQuantity) {
        row.quantity = quantity;
        row.total = times(quantity, unitCost, rules);
      } else {
        row.total = times(row.quantity, unitCost, rules);
      }

      const customProps = existingActivity[indexKey].data.customProperties;
      if (customProps) row.customProperties = customProps;
    }

    return row;
  }

  private setSelectedLineDataToRow(row, selectedLine): any {
    if (selectedLine.data.parentTask)
      row.parentTask = selectedLine.data.parentTask;
    if (selectedLine.data.parentStage)
      row.parentStage = selectedLine.data.parentStage;
    row.rowIndex = selectedLine.data.rowIndex;

    return row;
  }

  private createActivitySubItems = async (
    parentTask: string,
    activity: FirebaseDocument<RateLibraryTasks>,
    rateLibraryId: string,
    tasksList: any
  ): Promise<IAction[]> => {
    let newActions = new Array<IAction>();
    let allIds: any = [];
    for (let i = 0; i < activity.data.composition.length; i++) {
      const composition = activity.data.composition[i];
      const rowRef = WBSStore.WBSRef.collection("rows").doc();

      let defaultRow = this.createTaskDefaultRow(
        composition,
        i,
        parentTask,
        rateLibraryId
      );
      defaultRow = this.setExistingActivityDataToRow(
        defaultRow,
        rateLibraryId,
        composition.code
      );

      // Cria as propriedades do item se não já não houver
      // de uma ocorrência existente
      if (!defaultRow.customProperties) {
        defaultRow.customProperties =
          await this.createNeededCustomPropertiesOfRateLibraryItem(composition);
      }

      newActions.push(this.addActivityRow(rowRef, defaultRow));

      if (defaultRow.rowType === ERowType.TASK) {
        allIds.push({
          reference: composition.reference,
          parentTask: rowRef.id,
        });
      }
    }

    const subTasks = await ActivitySearchService.getRateLibraryTaskByArrayIds(
      allIds,
      tasksList
    );

    for (let i = 0; i < subTasks.length; i++) {
      let list = await this.createActivitySubItems(
        subTasks[i].parentTask,
        subTasks[i],
        rateLibraryId,
        tasksList
      );
      newActions.push(...list);
    }
    return newActions;
  };

  private createTaskDefaultRow(
    composition,
    rowIndex: number,
    parentTask: string,
    rateLibraryId: string
  ): any {
    const quantity = parseFloat(
      composition.coefficient.toString().replace(",", ".")
    );
    const rowType =
      composition.type !== "RESOURCE" ? ERowType.TASK : ERowType.RESOURCE;
    const defaultRow = WBSModels.getDefaultRow({
      description: composition.description,
      category: composition.class,
      rowIndex,
      rowType,
      quantity,
      unit: composition.unity,
      unitCost: composition.unityCost,
      total: composition.totalCost,
    });

    defaultRow.parentTask = parentTask;
    defaultRow.rateLibraryId = rateLibraryId;
    defaultRow.rateLibraryCode = composition.code;
    defaultRow.rateLibraryName = composition.description;
    if (composition.code) defaultRow.rowCode = composition.code;

    return defaultRow;
  }

  private addActivityRow(rowRef, rowData): IAction {
    WBSStore.insertRowAtEnd(rowRef.id, rowData);
    return {
      type: "set",
      args: [rowRef, rowData],
    };
  }

  // Atualiza os valores para o usuário
  async updateValues(response) {
    let resources = [...WBSStore.resources];
    let tasks = [...WBSStore.tasks];
    for (let i = 0; i < response.data.tasks.length; i++) {
      for (let j = tasks.length - 1; j >= 0; j--) {
        if (tasks[j].id == response.data.tasks[i]) {
          tasks.splice(j, 1);
          continue;
        }
      }
    }
    for (let i = 0; i < response.data.resources.length; i++) {
      for (let j = resources.length - 1; j >= 0; j--) {
        if (resources[j].id == response.data.resources[i]) {
          resources.splice(j, 1);
          continue;
        }
      }
    }

    WBSStore.resources = resources;
    WBSStore.tasks = tasks;
  }
}

let ActivitySearchStore = new Store();

export { ActivitySearchStore };



// WEBPACK FOOTER //
// ./src/modules/activitySearch/ActivitySearchStore.tsx